import React, { useState, useContext, useEffect } from "react";
import {
  Menu,
  ChevronLeft,
  Moon,
  Sun,
  MessageSquare,
  UserCircle,
  PanelLeft,
  PanelTop,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { cn } from "../lib/utils";

import MainListItems from "./MainListItems";
import TopNav from "./TopNav";
import SaudacaoDoUsuario from "../components/SaudacaoDoUsuario";
import BottomNav from "./BottomNav";
import MenuDeIcones from "./MenuDeIcones";
import { lerPreferencia, gravarPreferencia } from "../services/preferencias";
import InstalarApp from "../components/InstalarApp";
import AtualizacaoDisponivel from "../components/AtualizacaoDisponivel";
import NotificacoesPrompt from "../components/NotificacoesPrompt";
import NotificationsPopOver from "../components/NotificationsPopOver";
import VolumeDoAlerta from "../components/VolumeDoAlerta";
import SolicitacoesDeTransferencia from "../components/SolicitacoesDeTransferencia";
import GlobalSearch from "../components/GlobalSearch";
import UserModal from "../components/UserModal";
import Softphone from "../components/Softphone";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { useWhiteLabel } from "../context/WhiteLabel";

// 264 e não 240: com o recuo dos subitens, rótulos como "Cargos e permissões"
// e "Mensagens agendadas" chegavam ao limite e eram cortados com reticências.
// Nome de menu truncado obriga a adivinhar para onde o item leva.
const SIDEBAR_WIDTH = 264;
const SIDEBAR_WIDTH_COLLAPSED = 72;

// Onde o menu mora, no desktop: "lateral" (padrão, como sempre foi) ou "topo".
// Vai em cookie porque é assim que este projeto guarda preferência de tela —
// o localStorage saiu de propósito (ver services/preferencias.js).
const CHAVE_MODO_MENU = "layout:modoMenu";

function SidebarContent({ collapsed, drawerClose }) {
  // A marca da empresa, não a nossa: quem configurou nome e logo espera
  // vê-los aqui, e não "WhaTicket" fixo.
  const { appName, logoUrl } = useWhiteLabel();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 px-4">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={appName}
            className="h-8 w-8 shrink-0 rounded-lg object-contain"
          />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <MessageSquare className="h-4 w-4" />
          </div>
        )}
        {!collapsed && (
          <span className="truncate text-base font-semibold tracking-tight">
            {appName}
          </span>
        )}
      </div>
      <div className="scrollbar-discreta flex-1 overflow-y-auto py-2">
        <MainListItems drawerClose={drawerClose} collapsed={collapsed} />
      </div>
    </div>
  );
}

const LoggedInLayout = ({ children }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const { appName, logoUrl } = useWhiteLabel();

  // Só vale do md: para cima. No celular a navegação é a barra de baixo mais a
  // bandeja de ícones, e nada disso muda com esta preferência.
  const [modoMenu, setModoMenu] = useState(() =>
    lerPreferencia(CHAVE_MODO_MENU, "lateral") === "topo" ? "topo" : "lateral"
  );
  const modoTopo = modoMenu === "topo";

  const alternarModoMenu = () => {
    setModoMenu((anterior) => {
      const novo = anterior === "topo" ? "lateral" : "topo";
      gravarPreferencia(CHAVE_MODO_MENU, novo);
      return novo;
    });
  };

  useEffect(() => {
    const checkSize = () => {
      const mobile = document.body.offsetWidth < 600;
      setIsMobile(mobile);
      if (!mobile) setDrawerOpen(true);
    };
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  const drawerClose = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
  };

  const handleClickLogout = () => {
    handleLogout();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  const sidebarWidth = drawerOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED;

  return (
    // h-dvh, não h-screen: no celular o 100vh do `h-screen` conta a área que
    // a barra de endereço do navegador ocupa, então o rodapé da tela — a
    // caixa de digitar mensagem — ficava abaixo da parte visível e aparecia
    // cortado. O dvh acompanha a barra aparecendo e sumindo.
    <div className="flex h-dvh w-full overflow-hidden bg-background">
      {/* Barra lateral do desktop. No modo "topo" ela não é escondida por CSS:
          sai da árvore mesmo, senão o MainListItems continuaria montado e o
          conteúdo não ganharia a largura toda. */}
      {!modoTopo && (
        <aside
          className={cn(
            "hidden shrink-0 border-r bg-card transition-[width] duration-200 ease-in-out md:block"
          )}
          style={{ width: sidebarWidth }}
        >
          <SidebarContent collapsed={!drawerOpen} drawerClose={drawerClose} />
        </aside>
      )}

      {/* No celular não há gaveta lateral: o "Mais" da barra de baixo abre
          uma bandeja com todos os ícones. Gaveta que entra pelo lado é padrão
          de desktop — cobre o conteúdo por uma borda só e obriga a percorrer
          38 linhas verticais para achar uma tela. */}
      <MenuDeIcones
        aberto={mobileOpen}
        aoFechar={() => setMobileOpen(false)}
        aoAbrirPerfil={handleOpenUserModal}
        aoSair={handleClickLogout}
      />

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      <Softphone />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        {/* pt com a safe area: aberto como app na tela de início, o topo cai
            atrás do relógio e da bateria do iPhone. */}
        {/* Barra de cima: só do tablet para cima.
            No celular ela custava 56px de altura em toda tela para carregar
            um menu que agora é a barra de baixo e ações de conta que passaram
            para a bandeja do "Mais". Busca e avisos foram para a barra de
            baixo, que é onde o polegar alcança. */}
        <header className="hidden min-h-14 shrink-0 items-center gap-3 border-b bg-background px-4 pt-[env(safe-area-inset-top)] md:flex">
          {/* O menu completo, no celular.
              A barra de baixo cobre os quatro destinos do dia a dia, mas ela
              se recolhe dentro de uma conversa — e lá o menu ficava sem
              nenhuma porta de entrada. Este botão existe em toda tela, que é
              o que se espera de um sanduíche. */}
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 md:hidden"
            aria-label="Abrir menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          {/* Recolher a lateral só faz sentido quando ela existe. */}
          {!modoTopo && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:inline-flex"
              onClick={() => setDrawerOpen((prev) => !prev)}
            >
              <ChevronLeft
                className={cn(
                  "h-5 w-5 transition-transform",
                  !drawerOpen && "rotate-180"
                )}
              />
            </Button>
          )}

          {/* A marca aparece no topo do celular: sem a barra lateral fixa, a
              tela não dizia em que sistema a pessoa está. No modo topo ela
              precisa aparecer no desktop também, pelo mesmo motivo: quem
              carregava logo e nome era o cabeçalho da lateral, que sumiu. */}
          {modoTopo && (
            <div className="hidden shrink-0 items-center gap-2 md:flex">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={appName}
                  className="h-7 w-7 shrink-0 rounded-lg object-contain"
                />
              ) : (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <MessageSquare className="h-4 w-4" />
                </div>
              )}
              <span className="hidden truncate text-base font-semibold tracking-tight lg:inline">
                {appName}
              </span>
            </div>
          )}

          <span className="truncate text-base font-semibold tracking-tight md:hidden">
            {appName}
          </span>

          {/* No modo topo a navegação ocupa o espaço livre da barra; no modo
              lateral, esse espaço passou a levar a saudação — quem está
              usando, por qual empresa e até quando o acesso vale. */}
          {modoTopo ? <TopNav /> : <SaudacaoDoUsuario />}

          {/* O par sol/lua com o interruptor ocupava três espaços da barra —
              no celular sobrava pouco para busca e avisos, que são o que se
              usa. Ali ele vira um item do menu do usuário. */}
          <div className="hidden shrink-0 items-center gap-1 md:flex">
            <Sun className="h-4 w-4 text-muted-foreground" />
            <Switch checked={darkMode} onCheckedChange={toggleTheme} />
            <Moon className="h-4 w-4 text-muted-foreground" />
          </div>

          {user.id && <GlobalSearch />}
          {user.id && <NotificationsPopOver />}

          {/* Volume de todo alerta sonoro, num controle só. Ao lado do perfil
              porque é ajuste de quem está usando a máquina, não da empresa. */}
          {user.id && <VolumeDoAlerta />}

          {/* Pedido de transferência de um colega. Fica no layout porque
              chega a qualquer momento, esteja a pessoa em que tela estiver. */}
          {user.id && <SolicitacoesDeTransferencia />}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <UserCircle className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="md:hidden"
                onSelect={(e) => {
                  // Sem isto o menu fecha antes de o tema trocar, e o toque
                  // parece não ter feito nada.
                  e.preventDefault();
                  toggleTheme();
                }}
              >
                {darkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
                {darkMode ? "Tema claro" : "Tema escuro"}
              </DropdownMenuItem>
              {/* Onde o menu fica é preferência de aparência, da mesma família
                  do tema — e este já é o lugar do tema. Uma opção usada uma vez
                  na vida não merece espaço fixo na barra, e aqui ela fica no
                  mesmo canto em todas as telas. Só no desktop: no celular a
                  navegação é outra (barra de baixo + bandeja) e o item seria uma
                  chave que não liga nada. */}
              <DropdownMenuItem
                className="hidden md:flex"
                onSelect={(e) => {
                  // Igual ao tema: sem isto o menu fecha antes de a tela se
                  // remontar e o clique parece não ter feito nada.
                  e.preventDefault();
                  alternarModoMenu();
                }}
              >
                {modoTopo ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelTop className="h-4 w-4" />
                )}
                {modoTopo ? "Menu lateral" : "Menu no topo"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* pt com a safe area no celular: sem a barra de cima, o conteúdo
            passou a começar na primeira linha da tela — e aberto como
            aplicativo, ou num iPhone com entalhe, a primeira linha fica atrás
            do relógio e da bateria. Do md: para cima quem já cuida disso é o
            cabeçalho. */}
        <main className="flex min-h-0 flex-1 flex-col overflow-auto pt-[env(safe-area-inset-top)] md:pt-0">
          {children ? children : null}
        </main>

        <InstalarApp />
        <AtualizacaoDisponivel />
        <NotificacoesPrompt />

        <BottomNav onAbrirMenu={() => setMobileOpen(true)} />
      </div>
    </div>
  );
};

export default LoggedInLayout;
